import React, { useEffect, useState } from "react";
import CustomTextField from "../components/CustomTextField";
import CustomButton from "../components/CustomButton";
import ReusableHeader from "./ReusableHeader";
import "../styles/layout.css";
import { decodeJwt } from "../util/decodeJwt";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import { useDirectCustomerApi } from "../api/direct_customers";
import CustomDropdown from "../components/CustomDropdown";

export default function CustomerCreateOrUpdateWidget({ onSubmit }) {
  const { id } = useParams(); // 👈 fetch id from URL
  const { createDirectCustomer, updateDirectCustomer,getDirectCustomerById } = useDirectCustomerApi();
  const [formData, setFormData] = useState({
    name: "",
    phoneNumber: "",
    address: "",
    vendorId: "",
    password: "",

  });
  const [userId, setUserId] = useState("");
  const [loading, setLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // 🔑 Decode userId from token
    const token = localStorage.getItem("token");
    if (token) {
      const decoded = decodeJwt(token);
      setUserId(decoded?._id || "Unknown User");
    } else {
      setUserId("Unknown User");
    }
  }, []);

  useEffect(() => {
    // 🔑 If there's an id in the URL → fetch customer details
    async function fetchCustomer() {
      if (id) {
        try {
          setLoading(true);
          const result = await getDirectCustomerById(id);
          const customer = result.data;

          setFormData({
            name: customer.name || "",
            phoneNumber: customer.phoneNumber || "",
            address: customer.address || "",
            vendorId: customer.vendorId || "",
            password: "", // do not pre-fill password
          });

          setIsEditMode(true);
        } catch (error) {
          console.error(error.message);
          toast.error(error.message || "Failed to load customer");
        } finally {
          setLoading(false);
        }
      }
    }

    fetchCustomer();
  }, [id]);

  const handleChange = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async () => {
    const finalData = {
      ...formData,
      vendorId: userId, // overwrite with logged-in userId
    };

        // Validate required fields
    if (!finalData.name || !formData.phoneNumber) {
      toast.warn("Please fill in all required fields");
      return;
    }

    console.log("Submitted JSON:", finalData);

    try {
      setLoading(true);
      let result;

      if (isEditMode) {
        result = await updateDirectCustomer(id, finalData); // 👈 update with id from URL
        toast.success("Customer updated successfully!");
      } else {
        result = await createDirectCustomer(finalData);
        toast.success("Customer created successfully!");
      }

      if (onSubmit) onSubmit(result.data || finalData);
      navigate("/customers");
    } catch (error) {
      console.error(error.message);
      toast.error(error.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="customer-container">
      <ReusableHeader title={isEditMode ? "Edit Customer" : "Customer Management"} />

      <div className="customer-form-card">
        <div className="customer-columns">
          <div className="customer-column-left">
            <CustomTextField
              label="Customer Name"
              value={formData.name}
              onChange={(e) => handleChange("name", e.target.value)}
            />

            <CustomTextField
              label="Address"
              value={formData.address}
              onChange={(e) => handleChange("address", e.target.value)}
              multiline
              rows={5}
              minRows={5}
              style={{ minHeight: "120px" }}
            />
          </div>

          <div className="customer-column-right">
            <CustomTextField
              label="Phone Number"
              numericOnly={true} // ✅ restrict to digits only
              value={formData.phoneNumber}
              onChange={(e) => handleChange("phoneNumber", e.target.value)}
            />
            <CustomTextField 
              label="Password" 
              type="password" 
              value={formData.password} 
              onChange={(e) => handleChange("password", e.target.value)} />
          </div>
        </div>

        <div className="customer-buttons">
          <CustomButton
            label="Cancel"
            className="customer-cancel"
            onClick={() => navigate("/customers")}
          />
          <CustomButton
            label={
              loading
                ? isEditMode
                  ? "Updating..."
                  : "Creating..."
                : isEditMode
                ? "Update"
                : "Create"
            }
            className="customer-create"
            onClick={handleSubmit}
            disabled={loading}
          />
        </div>
      </div>
    </div>
  );
}